import React, { useMemo } from "react";
import EChart from "./Echart";

const lightenColor = (hex, percent) => {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;

  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? R : 255) * 0x10000 +
      (G < 255 ? G : 255) * 0x100 +
      (B < 255 ? B : 255)
    )
      .toString(16)
      .slice(1)
  );
};

const SunburstChart = ({ data = [] }) => {
  const baseColors = [
    "#1f4e79", // azul profundo
    "#2e7d32", // verde
    "#c62828", // rojo
    "#6a1b9a", // violeta
    "#ef6c00", // naranja
  ];

  const coloredData = useMemo(() => {
    return data.map((root, index) => {
      const baseColor = baseColors[index % baseColors.length];

      return {
        ...root,
        itemStyle: { color: baseColor },
        children: root.children?.map((child) => ({
          ...child,
          itemStyle: { color: lightenColor(baseColor, 20) },
          children: child.children?.map((leaf) => ({
            ...leaf,
            itemStyle: { color: lightenColor(baseColor, 40) },
          })),
        })),
      };
    });
  }, [data]);

  const totalEnergy = useMemo(() => {
    let total = 0;

    data.forEach((carrier) => {
      carrier.children?.forEach((group) => {
        group.children?.forEach((device) => {
          total += device.value || 0;
        });
      });
    });

    return total;
  }, [data]);

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "item",
        backgroundColor: "#ffffff",
        borderColor: "#dfe6f1",
        borderWidth: 1,
        textStyle: {
          color: "#2c3e50",
        },
        formatter: (params) => {
          const percent = params.percent
            ? `(${params.percent.toFixed(2)}%)`
            : "";

          return `
            <strong>${params.name}</strong><br/>
            Consumo: ${params.value ? params.value.toFixed(2) : 0} kWh<br/>
            ${percent}
          `;
        },
      },

      graphic: {
        type: "text",
        left: "center",
        top: "middle",
        style: {
          text: `${totalEnergy.toFixed(0)}\nkWh`,
          textAlign: "center",
          fill: "#2c3e50",
          fontSize: 14,
          fontWeight: "bold",
        },
      },

      series: [
        {
          type: "sunburst",
          data: coloredData,
          radius: ["15%", "90%"],
          sort: null,

          nodeClick: "zoomToNode",

          emphasis: {
            focus: "none",
            scale: true,
            label: {
              show: true,
            },
          },

          blur: {
            label: {
              show: true,
              opacity: 1,
            },
            itemStyle: {
              opacity: 1,
            },
          },

          label: {
            show: true,
            overflow: "truncate",
            minAngle: 8,
            fontSize: 12,
            color: "#2c3e50",
          },

          itemStyle: {
            borderWidth: 1,
            borderColor: "#ffffff",
          },

          levels: [
            {},
            {
              r0: "15%",
              r: "40%",
              label: {
                rotate: 0,
                fontWeight: "bold",
                fontSize: 14,
                formatter: (params) => {
                  let groupValue = 0;

                  params.data.children?.forEach((subgroup) => {
                    subgroup.children?.forEach((device) => {
                      groupValue += device.value || 0;
                    });
                  });

                  const percent =
                    totalEnergy > 0
                      ? ((groupValue / totalEnergy) * 100).toFixed(1)
                      : 0;

                  return `${params.name}\n${percent}%`;
                },
              },
            },
            {
              r0: "40%",
              r: "65%",
              label: {
                rotate: "tangential",
              },
            },
            {
              r0: "65%",
              r: "90%",
              label: {
                show: false,
              },
            },
          ],
        },
      ],
    }),
    [data],
  );

  return <EChart option={option} style={{ width: "100%", height: 500 }} />;
};

export default SunburstChart;
